import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { Person as PassengerIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { api } from '../../../../services/api';

interface TripPassenger {
  id?: number;
  passengerName: string;
  contactNumber: string;
  email: string;
  pickupInfo?: string;
  dropInfo?: string;
  specialRequirements?: string;
  tripForSelf: boolean;
}

interface TripPassengersSectionProps {
  passengers: TripPassenger[];
  onUpdate: (updatedPassengers: TripPassenger[]) => Promise<void>;
  onRefreshTripDetails?: () => Promise<void>; // Add callback to refresh parent component
  loading?: boolean;
  tripCode: string;
  customerName?: string;
  customerMobile?: string;
  customerEmail?: string;
  disabled?: boolean;
}

const TripPassengersSection: React.FC<TripPassengersSectionProps> = ({
  passengers,
  onUpdate,
  onRefreshTripDetails,
  loading = false,
  tripCode,
  customerName = '',
  customerMobile = '',
  customerEmail = '',
  disabled = false
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPassenger, setEditingPassenger] = useState<TripPassenger | null>(null);
  const [localPassengers, setLocalPassengers] = useState<TripPassenger[]>(passengers);
  const [passengerForm, setPassengerForm] = useState({
    passengerName: '',
    contactNumber: '',
    email: '',
    pickupInfo: '',
    dropInfo: '',
    specialRequirements: '',
    tripForSelf: false
  });
  const [saving, setSaving] = useState(false);
  const [loadingNewPassenger, setLoadingNewPassenger] = useState(false);
  
  // Add ref for scroll position preservation
  const passengersSectionRef = useRef<HTMLDivElement>(null);
  const [shouldPreserveScroll, setShouldPreserveScroll] = useState(false);
  
  
  

  // Update local passengers when props change
  React.useEffect(() => {
    setLocalPassengers(passengers);
  }, [passengers]);

  // Preserve scroll position after operations complete
  useEffect(() => {
    if (shouldPreserveScroll && passengersSectionRef.current) {
      // Small delay to ensure DOM updates are complete
      setTimeout(() => {
        passengersSectionRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        setShouldPreserveScroll(false);
      }, 100);
    }
  }, [localPassengers, isDialogOpen, loadingNewPassenger, shouldPreserveScroll]);

  const totalPassengers = localPassengers.length;
  
  // Check if there are passengers with valid IDs (from database)
  const hasPassengersWithValidIds = localPassengers.some(passenger => 
    passenger.id && passenger.id.toString().length <= 10
  );
  
  // Check if this is the first passenger being added (no existing passengers)
  const isFirstPassenger = localPassengers.length === 0;

  const SectionHeader = () => (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        backgroundColor: '#464545',
        color: 'white',
        p: 2,
        borderRadius: '8px 8px 0 0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PassengerIcon />
        <Typography variant="h6" fontWeight="bold">
          TRIP PASSENGERS ({totalPassengers})
        </Typography>
      </Box>
      <Tooltip 
        title={
          disabled 
            ? "Cannot add passengers - trip is cancelled"
            : hasPassengersWithValidIds && !isFirstPassenger 
            ? "Cannot add more passengers when trip has existing passenger details" 
            : "Add a new passenger"
        }
        arrow
      >
        <span>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPassenger}
            disabled={saving || loadingNewPassenger || disabled || (hasPassengersWithValidIds && !isFirstPassenger)}
            sx={{ 
              backgroundColor: (disabled || (hasPassengersWithValidIds && !isFirstPassenger)) ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
              '&:hover': { backgroundColor: (disabled || (hasPassengersWithValidIds && !isFirstPassenger)) ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.3)' },
              '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }
            }}
          >
            {loadingNewPassenger ? 'Adding...' : 'Add Passenger'}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );

  const handleEditPassenger = (passenger: TripPassenger) => {
    // Check if passenger has a valid server ID (not a temporary timestamp ID)
    if (!passenger.id || passenger.id.toString().length > 10) {
      alert('Cannot edit this passenger. Please refresh the page and try again.');
      return;
    }
    
    setEditingPassenger(passenger);
    setPassengerForm({
      passengerName: passenger.passengerName || '',
      contactNumber: passenger.contactNumber || '',
      email: passenger.email || '',
      pickupInfo: passenger.pickupInfo || '',
      dropInfo: passenger.dropInfo || '',
      specialRequirements: passenger.specialRequirements || '',
      tripForSelf: passenger.tripForSelf || false
    });
    setIsDialogOpen(true);
  };

  const handleAddPassenger = () => {
    setEditingPassenger(null);
    setPassengerForm({
      passengerName: '',
      contactNumber: '',
      email: '',
      pickupInfo: '',
      dropInfo: '',
      specialRequirements: '',
      tripForSelf: false
    });
    setIsDialogOpen(true);
  };

  // Handle trip for self checkbox change
  const handleTripForSelfChange = (checked: boolean) => {
    
    
    
    if (checked) {
      // Prefill with customer details when "Trip for self" is checked
      const updatedForm = {
        ...passengerForm,
        tripForSelf: true,
        passengerName: customerName && customerName.trim() !== '' ? customerName : passengerForm.passengerName,
        contactNumber: customerMobile && customerMobile.trim() !== '' ? customerMobile : passengerForm.contactNumber,
        email: customerEmail && customerEmail.trim() !== '' ? customerEmail : passengerForm.email
      };
      
      
      setPassengerForm(updatedForm);
    } else {
      // Clear the form when "Trip for self" is unchecked (except if user is editing)
      if (!editingPassenger) {
        setPassengerForm(prev => ({
          ...prev,
          tripForSelf: false,
          passengerName: '',
          contactNumber: '',
          email: ''
        }));
      } else {
        // For editing, just uncheck the tripForSelf
        setPassengerForm(prev => ({
          ...prev,
          tripForSelf: false
        }));
      }
    }
  };

  const handleSavePassenger = async () => {
    let originalPassenger: TripPassenger | null = null;
    
    try {
      setSaving(true);
      setShouldPreserveScroll(true); // Set flag to preserve scroll
      
      if (!tripCode) {
        alert('Trip Code is required');
        setShouldPreserveScroll(false); // Reset flag on validation error
        return;
      }

      if (!passengerForm.passengerName || !passengerForm.contactNumber) {
        alert('Passenger name and contact number are required');
        setShouldPreserveScroll(false); // Reset flag on validation error
        return;
      }
      
      const requestBody = {
        passengerName: passengerForm.passengerName,
        contactNumber: passengerForm.contactNumber,
        email: passengerForm.email || '',
        pickupInfo: passengerForm.pickupInfo || '',
        dropInfo: passengerForm.dropInfo || '',
        specialRequirements: passengerForm.specialRequirements || '',
        tripForSelf: passengerForm.tripForSelf
      };

      if (editingPassenger && editingPassenger.id) {
        // Editing existing passenger - check if passenger has a valid ID
        originalPassenger = editingPassenger;
        const updatedPassenger = {
          ...editingPassenger,
          ...requestBody
        };

        // Optimistically update the passenger in local state
        setLocalPassengers(prev => prev.map(passenger => 
          passenger.id === editingPassenger.id ? updatedPassenger : passenger
        ));

        // Close dialog and reset form
        setIsDialogOpen(false);
        setEditingPassenger(null);
        setPassengerForm({
          passengerName: '',
          contactNumber: '',
          email: '',
          pickupInfo: '',
          dropInfo: '',
          specialRequirements: '',
          tripForSelf: false
        });

        // Make API call to update existing passenger using updateTripPassenger
        await api.updateTripPassenger(tripCode, editingPassenger.id, requestBody);
        
        // Clear cache to ensure fresh data on next navigation
        api.clearCache(`getTripDetails_${tripCode}`);
        api.clearCache('getAllTrips'); // Also clear trips list cache
      } else {
        // Adding new passenger
        // Close dialog and reset form first
        setIsDialogOpen(false);
        setEditingPassenger(null);
        setPassengerForm({
          passengerName: '',
          contactNumber: '',
          email: '',
          pickupInfo: '',
          dropInfo: '',
          specialRequirements: '',
          tripForSelf: false
        });

        // Show loading state for new passenger
        setLoadingNewPassenger(true);

        // Make API call to add new passenger
        const addedPassenger = await api.addTripPassenger(tripCode, requestBody);
        
        
        // Clear cache to ensure fresh data
        api.clearCache(`getTripDetails_${tripCode}`);
        api.clearCache('getAllTrips');

        // If this is the first passenger or we need fresh data, trigger parent refresh
        if (isFirstPassenger || !addedPassenger || typeof addedPassenger !== 'object' || !('id' in addedPassenger)) {
          
          if (onRefreshTripDetails) {
            await onRefreshTripDetails();
          } else {
            // Fallback: try to refresh from server directly
            try {
              const freshTripData = await api.getTripDetails(tripCode);
              if (freshTripData && typeof freshTripData === 'object' && 'passengers' in freshTripData) {
                const passengers = (freshTripData as any).passengers;
                if (Array.isArray(passengers)) {
                  setLocalPassengers(passengers);
                  
                }
              }
            } catch (refreshError) {
              console.error('Could not refresh passenger data:', refreshError);
              alert('Passenger added but could not refresh the list. Please reload the page.');
            }
          }
        } else {
          // Add the new passenger with real ID from server to local state
          const newPassengerWithRealId = {
            id: (addedPassenger as any).id,
            ...requestBody
          };
          setLocalPassengers(prev => [...prev, newPassengerWithRealId]);
        }
        
        // Hide loading state
        setLoadingNewPassenger(false);
      }

    } catch (error: any) {
      // Hide loading state on error
      setLoadingNewPassenger(false);
      
      // Revert optimistic update on error (only for edit operations)
      if (editingPassenger && originalPassenger) {
        // Restore original passenger for edit operations
        setLocalPassengers(prev => prev.map(passenger => 
          passenger.id === editingPassenger.id ? originalPassenger! : passenger
        ));
      }
      // For add operations, no rollback needed since we don't do optimistic updates
      alert(`Error saving passenger: ${error?.response?.data?.message || error?.message || error}`);
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setSaving(false);
      setLoadingNewPassenger(false); // Ensure loading state is cleared
    }
  };

  const handleDeletePassenger = async (passenger: TripPassenger) => {
    if (!passenger.id) {
      alert('Cannot delete passenger without ID');
      return;
    }

    // Check if passenger has a valid server ID (not a temporary timestamp ID)
    if (passenger.id.toString().length > 10) {
      alert('Cannot delete this passenger. Please refresh the page and try again.');
      return;
    }

    // Store original passengers for rollback
    const originalPassengers = [...localPassengers];

    try {
      setSaving(true);
      setShouldPreserveScroll(true); // Set flag to preserve scroll
      
      // Optimistically remove passenger from local state
      setLocalPassengers(prev => prev.filter(p => p.id !== passenger.id));

      // Make API call to delete passenger
      await api.removeTripPassenger(tripCode, passenger.id);
      
      // Clear cache to ensure fresh data on next navigation
      api.clearCache(`getTripDetails_${tripCode}`);
      api.clearCache('getAllTrips'); // Also clear trips list cache

    } catch (error: any) {
      // Revert optimistic update on error
      setLocalPassengers(originalPassengers);
      alert(`Error deleting passenger: ${error?.response?.data?.message || error?.message || error}`);
      setShouldPreserveScroll(false); // Reset flag on error
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card ref={passengersSectionRef} sx={{ mb: 2, backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
        <SectionHeader />
        <CardContent sx={{ backgroundColor: '#161616', color: 'white' }}>
          {loadingNewPassenger ? (
            // Show loading state when adding new passenger
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={40} sx={{ color: '#2563eb', mb: 2 }} />
              <Typography variant="body1" color="white">
                Adding passenger...
              </Typography>
            </Box>
          ) : localPassengers.length > 0 ? (
            <List sx={{ p: 0 }}>
              {localPassengers.map((passenger, index) => (
                <ListItem 
                  key={passenger.id || index}
                  sx={{ px: 0 }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton 
                        size="small"
                        onClick={() => handleEditPassenger(passenger)}
                        sx={{ color: '#3b82f6' }}
                        disabled={saving || loadingNewPassenger || disabled || !passenger.id || passenger.id.toString().length > 10}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => handleDeletePassenger(passenger)}
                        sx={{ color: '#ef4444' }}
                        disabled={saving || loadingNewPassenger || disabled || !passenger.id || passenger.id.toString().length > 10}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {/* <Chip 
                            label={passenger.tripForSelf ? 'SELF' : 'OTHER'}
                            size="small"
                            sx={{ 
                              backgroundColor: passenger.tripForSelf ? '#10b981' : '#6366f1',
                              color: 'white',
                              fontSize: '0.7rem'
                            }}
                          /> */}
                          {(!passenger.id || passenger.id.toString().length > 10) && (
                            <Chip 
                              label="DATA ISSUE"
                              size="small"
                              sx={{ 
                                backgroundColor: '#ef4444',
                                color: 'white',
                                fontSize: '0.6rem'
                              }}
                            />
                          )}
                          <Typography variant="subtitle1" fontWeight="bold" color="white">
                            {passenger.passengerName}
                          </Typography>
                          <Typography variant="subtitle2" color="#3b82f6">
                            {passenger.contactNumber}
                          </Typography>
                        </Box>
                        {(passenger.pickupInfo || passenger.dropInfo) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 1 }}>
                            {passenger.pickupInfo && (
                              <Typography variant="body2" color="#10b981">
                                Pickup: {passenger.pickupInfo}
                              </Typography>
                            )}
                            {passenger.dropInfo && (
                              <Typography variant="body2" color="#f59e0b">
                                Drop: {passenger.dropInfo}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          {passenger.email && `Email: ${passenger.email}`}
                          {passenger.specialRequirements && ` • Special: ${passenger.specialRequirements}`}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                No passengers added for this trip
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Click "Add Passenger" to add the first passenger
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Passenger Dialog */}
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1a1a1a', color: 'white' }}>
          {editingPassenger ? 'Edit Passenger' : 'Add Passenger'}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#2a2a2a', color: 'white' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Passenger Name *"
              value={passengerForm.passengerName}
              onChange={(e) => setPassengerForm(prev => ({ ...prev, passengerName: e.target.value }))}
              variant="outlined"
              disabled={disabled}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  color: 'white',
                  '& fieldset': { borderColor: '#666' },
                  '&:hover fieldset': { borderColor: '#999' },
                  '&.Mui-focused fieldset': { borderColor: '#2563eb' }
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
              }}
            />
            
            <TextField
              fullWidth
              label="Contact Number *"
              value={passengerForm.contactNumber}
              onChange={(e) => setPassengerForm(prev => ({ ...prev, contactNumber: e.target.value }))}
              variant="outlined"
              disabled={disabled}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  color: 'white',
                  '& fieldset': { borderColor: '#666' },
                  '&:hover fieldset': { borderColor: '#999' },
                  '&.Mui-focused fieldset': { borderColor: '#2563eb' }
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
              }}
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={passengerForm.email}
              onChange={(e) => setPassengerForm(prev => ({ ...prev, email: e.target.value }))}
              variant="outlined"
              disabled={disabled}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  color: 'white',
                  '& fieldset': { borderColor: '#666' },
                  '&:hover fieldset': { borderColor: '#999' },
                  '&.Mui-focused fieldset': { borderColor: '#2563eb' }
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
              }}
            />

            <TextField
              fullWidth
              label="Pickup Info"
              value={passengerForm.pickupInfo}
              onChange={(e) => setPassengerForm(prev => ({ ...prev, pickupInfo: e.target.value }))}
              variant="outlined"
              disabled={disabled}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  color: 'white',
                  '& fieldset': { borderColor: '#666' },
                  '&:hover fieldset': { borderColor: '#999' },
                  '&.Mui-focused fieldset': { borderColor: '#2563eb' }
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
              }}
            />

            <TextField
              fullWidth
              label="Drop Info"
              value={passengerForm.dropInfo}
              onChange={(e) => setPassengerForm(prev => ({ ...prev, dropInfo: e.target.value }))}
              variant="outlined"
              disabled={disabled}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  color: 'white',
                  '& fieldset': { borderColor: '#666' },
                  '&:hover fieldset': { borderColor: '#999' },
                  '&.Mui-focused fieldset': { borderColor: '#2563eb' }
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
              }}
            />

            <TextField
              fullWidth
              label="Special Requirements"
              multiline
              rows={3}
              value={passengerForm.specialRequirements}
              onChange={(e) => setPassengerForm(prev => ({ ...prev, specialRequirements: e.target.value }))}
              variant="outlined"
              disabled={disabled}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  color: 'white',
                  '& fieldset': { borderColor: '#666' },
                  '&:hover fieldset': { borderColor: '#999' },
                  '&.Mui-focused fieldset': { borderColor: '#2563eb' }
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={passengerForm.tripForSelf}
                  onChange={(e) => handleTripForSelfChange(e.target.checked)}
                  disabled={disabled}
                  sx={{ 
                    color: 'rgba(255,255,255,0.7)',
                    '&.Mui-checked': { color: '#2563eb' }
                  }}
                />
              }
              label="Trip for self"
              sx={{ color: 'rgba(255,255,255,0.7)' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#2a2a2a' }}>
          <Button 
            onClick={() => setIsDialogOpen(false)}
            sx={{ color: 'white' }}
            disabled={saving || loadingNewPassenger || disabled}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSavePassenger}
            variant="contained"
            sx={{ backgroundColor: '#2563eb' }}
            disabled={saving || loadingNewPassenger || disabled || !passengerForm.passengerName || !passengerForm.contactNumber}
          >
            {saving || loadingNewPassenger ? <CircularProgress size={20} /> : (editingPassenger ? 'Update Passenger' : 'Add Passenger')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TripPassengersSection;
